import { DOCUMENT } from '@angular/common';
import { Injectable, Inject } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class GlobalNumberStepperService {
  private observer?: MutationObserver;
  private initialized = false;

  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  init(): void {
    if (this.initialized || !this.isTouchDevice()) {
      return;
    }

    this.initialized = true;

    // Run after initial render so lazy templates are still handled safely.
    requestAnimationFrame(() => {
      this.enhanceRoot(this.document.body);
      this.observeDom();
    });
  }

  private isTouchDevice(): boolean {
    //return true; // Force enable for testing
    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      window.matchMedia('(pointer: coarse)').matches
    );
  }

  private observeDom(): void {
    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            this.enhanceElement(node);
          }
        });
      }
    });

    this.observer.observe(this.document.body, {
      childList: true,
      subtree: true,
    });
  }

  private enhanceRoot(root: HTMLElement): void {
    this.enhanceElement(root);
    root.querySelectorAll('input[type="number"]').forEach((el) => {
      this.decorateInput(el as HTMLInputElement);
    });
  }

  private enhanceElement(element: HTMLElement): void {
    if (element.matches('input[type="number"]')) {
      this.decorateInput(element as HTMLInputElement);
    }

    element.querySelectorAll('input[type="number"]').forEach((el) => {
      this.decorateInput(el as HTMLInputElement);
    });
  }

  private decorateInput(input: HTMLInputElement): void {
    if (input.dataset['touchStepperInit'] === 'true' || this.shouldIgnoreInput(input)) {
      return;
    }

    const parent = input.parentElement;
    if (!parent) {
      return;
    }

    const wrapper = this.document.createElement('span');
    wrapper.className = 'global-number-stepper';

    const minusBtn = this.document.createElement('button');
    minusBtn.type = 'button';
    minusBtn.className = 'stepper-btn stepper-btn-minus';
    minusBtn.setAttribute('aria-label', 'Decrement value');
    minusBtn.textContent = '-';

    const plusBtn = this.document.createElement('button');
    plusBtn.type = 'button';
    plusBtn.className = 'stepper-btn stepper-btn-plus';
    plusBtn.setAttribute('aria-label', 'Increment value');
    plusBtn.textContent = '+';

    parent.insertBefore(wrapper, input);
    wrapper.appendChild(minusBtn);
    wrapper.appendChild(input);
    wrapper.appendChild(plusBtn);

    input.dataset['touchStepperInit'] = 'true';

    minusBtn.addEventListener('click', () => this.changeValue(input, -1));
    plusBtn.addEventListener('click', () => this.changeValue(input, 1));
  }

  private shouldIgnoreInput(input: HTMLInputElement): boolean {
    if (
      input.hasAttribute('data-stepper-ignore') ||
      input.hasAttribute('stepper-ignore') ||
      input.classList.contains('no-touch-stepper')
    ) {
      return true;
    }

    if (input.dataset['stepperIgnore'] === 'true') {
      return true;
    }

    return Boolean(input.closest('[data-stepper-ignore], [stepper-ignore], .no-touch-stepper'));
  }

  private changeValue(input: HTMLInputElement, direction: 1 | -1): void {
    if (input.disabled || input.readOnly) {
      return;
    }

    const step = this.readNumber(input.getAttribute('step')) ?? 1;
    const min = this.readNumber(input.getAttribute('min'));
    const max = this.readNumber(input.getAttribute('max'));
    const current = this.readNumber(input.value) ?? min ?? 0;

    let next = current + step * direction;

    if (min !== null) {
      next = Math.max(min, next);
    }

    if (max !== null) {
      next = Math.min(max, next);
    }

    const precision = this.countDecimals(step);
    const normalized = precision > 0 ? Number(next.toFixed(precision)) : Math.round(next);

    input.value = String(normalized);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  private readNumber(value: string | null): number | null {
    if (value === null || value.trim() === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private countDecimals(value: number): number {
    const text = value.toString();
    const dot = text.indexOf('.');
    return dot === -1 ? 0 : text.length - dot - 1;
  }
}